import React, { FC, ReactText } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelect } from 'downshift'
import cx from 'classnames'
import { Icon } from 'components/Icon'
import { Text } from 'components/Text'
import styles from './Select.module.scss'

type SelectOption = {
  label: string
  value: ReactText
}

type SelectProps = {
  onChange: (value?: SelectOption) => void
  options: SelectOption[]
  value?: SelectOption
  className?: string
}

export const Select: FC<SelectProps> = ({ className, onChange, options, value }) => {
  const { t } = useTranslation()
  const selectValue = value ? value : options[0]
  const selectClasses = cx(styles.select, className)
  const itemToString = (item: SelectOption) => (item ? item.label : '')

  const {
    highlightedIndex,
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
  } = useSelect({
    selectedItem: selectValue,
    items: options,
    itemToString,
    onSelectedItemChange: (item) => onChange(item.selectedItem),
  })

  return (
    <div className={selectClasses}>
      <button
        type="button"
        {...getToggleButtonProps({
          className: cx({
            [styles.button]: true,
            [styles.isOpen]: isOpen,
          }),
          'aria-label': isOpen ? t('select.close') : t('select.open'),
        })}
      >
        {selectedItem && <Text className={styles.value}>{itemToString(selectedItem)}</Text>}

        <span className={styles.arrowContainer}>
          <Icon name="arrow-down-s-fill" size="lg" />
        </span>
      </button>
      <ul
        {...getMenuProps({
          className: styles.menu,
        })}
      >
        {isOpen &&
          options.map((option, index) => {
            return (
              <li
                className={styles.menuItem}
                {...getItemProps({
                  item: option,
                  index,
                  key: option.label,
                  className: cx({
                    [styles.menuItem]: true,
                    [styles.selected]: selectedItem && option.value === selectedItem.value,
                    [styles.highlighted]: highlightedIndex === index,
                  }),
                })}
              >
                {option.label}
              </li>
            )
          })}
      </ul>
    </div>
  )
}

Select.displayName = 'Select'
